import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  calculateOverlapRate,
  chromosomeRegion,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  switchVariantClass,
  unique,
  switchACMG
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent?: any
  setOpen?: any
  setHistoryOpen?: any
  setCommentsOpen?: any
  setTranscript_list?: any
  handleMenuClick?: any
  getColumnSearchProps?: any
  setFilterGeneOpen?: any
  setMenuType?: any
  setFilterGeneData?: any
  getFilterGene?: any
  setIndexOpen?: any
  setGeneIndexOpen?: any
  setGeneOpen?: any
  setAcmgOpen?: any
  setOMIMOpen?: any
  setIgvOpen?: any
  setIgvLocus?: any
  setCnvScipOpen?: any
  setCnvAllOpen?: any
  setCaseNumberInfoOpen?: any
  setClingenOpen?: any
  VariationGeneRef?: any
  analysisInfo?: any
  query?: any
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const columns1CNV = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    if (props.analysisInfo.pipelineVersion) {
      return [
        {
          accessorKey: 'chrom',
          id: 'chrom',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Chrom')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <TypographyA>{row.original.chrom}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'start',
          id: 'start',
          enableSorting: false,
          header: () => (
            <Stack
              maxWidth={'100%'}
              justifyContent="start"
              alignItems="center"
              direction={'row'}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Start')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('End ')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                sx={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  //
                  props.setIgvOpen && props.setIgvOpen(true)

                  props.setCurrent(row.original)

                  props.setIgvLocus &&
                    props.setIgvLocus(
                      `chr${row.original.chrom}:${row.original.start}-${row.original.end}`
                    )
                }}
              >
                {row.original.tempCnvManual &&
                row.original.tempCnvManual.type ? (
                  <TypographyA>{t('Merge')}</TypographyA>
                ) : (
                  ''
                )}

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {row.original.start}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {row.original.end}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'Chromosomearm',
          id: 'Chromosomearm',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Chromosome arm')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="center"
              >
                <TypographyA>{row.original.Chromosomearm}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'cn',
          id: 'cn',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Copy Number(CN)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="center"
              >
                <TypographyA>{row.original.cn}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'sv_type',
          id: 'sv_type ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Type')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original.sv_type}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'gene1',
          id: 'gene1',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'下拉箭头查看基因详情'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('Gene')}`}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const gene = row.original?.gene
            const scip_gene_info_data = row.original?.scip_gene_info ?? []

            return (
              <Stack
                sx={{
                  position: 'relative'
                }}
                maxWidth={'160px'}
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Stack
                  maxWidth={'130px'}
                  width={'100%'}
                  marginTop={'12px'}
                  direction="column"
                  alignItems="start"
                  sx={{
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    props.setCnvAllOpen && props.setCnvAllOpen(true)

                    props.setCurrent(row.original)
                  }}
                >
                  <Tooltip title={`${gene}`} placement="top-start">
                    <TypographyA
                      sx={{
                        width: '100%',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-all',
                        fontWeight: 500
                      }}
                    >
                      {gene ? (
                        <>
                          <span>{`${gene}`}</span>
                        </>
                      ) : null}
                    </TypographyA>
                  </Tooltip>
                </Stack>

                {scip_gene_info_data.length ? (
                  <IconButton
                    onClick={(e) => {
                      props.setCurrent(row.original)

                      props.handleMenuClick &&
                        props.handleMenuClick(e, 'scip_gene')
                    }}
                    sx={{
                      color: 'inherit',
                      padding: 0

                      // position: 'absolute',
                      // right: 0,
                      // bottom: '-1rem'
                    }}
                    size="small"
                  >
                    <MenuDown />
                  </IconButton>
                ) : (
                  ''
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'score_details',
          id: 'score_details ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('ACMG evidences')}(Annotsv)`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`ACMG ${t('score')}`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Length (bp)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.score_details}</TypographyA>

                <TypographyA>{row.original.total_score}</TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {typeof row.original.sv_length === 'number'
                    ? Math.round(row.original.sv_length)
                    : row.original.sv_length}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'ccnv_classification',
          id: 'ccnv_priority',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'双软件注释(classifyCNV、annotsv)'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('ACMG')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const classification = row.original.classification
            const flag =
              (classification === 'LP' || classification === 'PAT') &&
              classification !== row.original.ccnv_classification

            const bgcolor = GLScolor.find(
              (i) =>
                i.key ===
                switchACMG(row.original.ccnv_classification)?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key ===
                    switchACMG(row.original.ccnv_classification)?.toLowerCase()
                )?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'rgba(0, 0, 0)'

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="row"
                alignItems="center"
                justifyContent={'center'}
              >
                {row.original.ccnv_classification &&
                row.original.ccnv_classification != '.' ? (
                  <Chip
                    sx={{
                      bgcolor: bgcolor,
                      color:
                        bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
                      justifyContent: 'start',
                      fontSize: '0.75rem'
                    }}
                    label={`${row.original.ccnv_classification}`}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setCnvScipOpen && props.setCnvScipOpen(true)
                    }}
                    size="small"
                  />
                ) : (
                  row.original.ccnv_classification
                )}

                {flag ? (
                  <Tooltip
                    placement="top-start"
                    title={`AnnotSV:${classification}`}
                  >
                    <Box
                      sx={{
                        width: '10px',
                        height: '10px',
                        bgcolor: 'red',
                        borderRadius: '50%',
                        display: 'inline-block'
                      }}
                    ></Box>
                  </Tooltip>
                ) : (
                  ''
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'scip_highlights_data',
          id: 'scip_highlights_data',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'良性信息(如高人群频率等)'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('负面信息情况')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const scip_highlights = row.original?.scip_highlights
              ? row.original?.scip_highlights
              : {}
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>
                  {scip_highlights?.negative?.filter(
                    (i: string) => !i.includes('没有异常读取')
                  ).length
                    ? t('true')
                    : t('false')}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'inheritance',
          id: 'inheritance',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="center" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('OMIM')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const omim_genes = row.original.omim_genes ?? []
            const omim_curate_phenotypes = omim_genes
              .map((i: any) => i?.omim_curate_phenotypes)
              .flat()
            const omim_phenotypes = omim_genes
              .map((i: any) => i?.omim_phenotypes)
              .flat()
            if (
              i18n &&
              i18n.language === 'zh' &&
              omim_curate_phenotypes &&
              omim_curate_phenotypes.length
            ) {
              let OMIMValue = omim_curate_phenotypes

              OMIMValue = unique(OMIMValue, 'disease')

              let bgcolor = color.find(
                (i) => i.key === row.original.gls_classification?.toLowerCase()
              )
                ? (color.find(
                    (i) =>
                      i.key === row.original.gls_classification?.toLowerCase()
                  )?.color as any)
                : 'rgba(0, 0, 0)'

              // loss_of_function等于1 并且gls_classification不等于P/LP
              if (
                row.original.gls_classification?.toLowerCase() === 'pathogenic'
              ) {
                //
              } else if (
                row.original.gls_classification?.toLowerCase() ===
                'likely pathogenic'
              ) {
                //
              } else if (row.original.loss_of_function === 1)
                bgcolor = '#f5d18c'

              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {OMIMValue && OMIMValue.length ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {OMIMValue.map((item: any, index: any) => (
                            <TypographyA
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: '1rem',
                                maxWidth: isWindow()
                                  ? '400px !important'
                                  : '200px !important'
                              }}
                              key={`${item.disease}_${index}`}
                            >
                              {`(${
                                item.genetic_mode ? item.genetic_mode : '.'
                              })-${
                                i18n && i18n.language === 'zh'
                                  ? item.disease
                                  : ''
                              }`}
                            </TypographyA>
                          ))}
                        </React.Fragment>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: isWindow() ? '400px' : '200px'
                        }
                      }}
                    >
                      <Box>
                        {OMIMValue.slice(0, 2).map((items: any, index: any) => {
                          return (
                            <TypographyA
                              sx={{
                                width: '100%',
                                maxWidth: 200,
                                lineHeight: '24px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color:
                                  bgcolor === 'rgb(109,56,149,0.8)' ||
                                  bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgba(229,38,35,0.8)'
                                    ? 'white'
                                    : '#23527c',
                                mb: '4px'
                              }}
                              onClick={() => {
                                props.setCurrent({
                                  ...row.original,
                                  omim_curate_phenotypes,
                                  omim_phenotypes
                                })

                                props.setOMIMOpen(true)
                              }}
                              key={`${items.disease}_${index}`}
                            >
                              <Chip
                                label={`${
                                  items.genetic_mode ? items.genetic_mode : '.'
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}
                              />

                              <span
                                style={{
                                  lineHeight: '24px'
                                }}
                              >{`${
                                i18n && i18n.language === 'zh'
                                  ? items.disease + '-'
                                  : ''
                              }${items.disease}`}</span>
                            </TypographyA>
                          )
                        })}

                        {/* {OMIMValue.length > 3 ? '...' : ''} */}
                      </Box>
                    </HtmlTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            } else {
              let OMIMValue =
                omim_phenotypes?.map((item) => {
                  const inheritances: string[] = []
                  const clinicalSynopsis = isJson(item.clinicalSynopsis)
                    ? JSON.parse(item.clinicalSynopsis)
                    : item.clinicalSynopsis

                  if (
                    item &&
                    clinicalSynopsis &&
                    clinicalSynopsis.INHERITANCE &&
                    Array.isArray(clinicalSynopsis.INHERITANCE)
                  ) {
                    clinicalSynopsis.INHERITANCE.forEach((str: string) =>
                      inheritances.push(str.toLowerCase())
                    )
                  }
                  const inheritance = formatVariantInheritance(
                    Array.from([...new Set(inheritances)]).join(',')
                  )

                  return {
                    ...item,
                    inheritance
                  }
                }) ?? []

              OMIMValue = unique(OMIMValue, 'phenoName')

              let bgcolor = color.find(
                (i) => i.key === row.original.gls_classification?.toLowerCase()
              )
                ? (color.find(
                    (i) =>
                      i.key === row.original.gls_classification?.toLowerCase()
                  )?.color as any)
                : 'rgba(0, 0, 0)'

              // loss_of_function等于1 并且gls_classification不等于P/LP
              if (
                row.original.gls_classification?.toLowerCase() === 'pathogenic'
              ) {
                //
              } else if (
                row.original.gls_classification?.toLowerCase() ===
                'likely pathogenic'
              ) {
                //
              } else if (row.original.loss_of_function === 1)
                bgcolor = '#f5d18c'

              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {OMIMValue && OMIMValue.length ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {OMIMValue.map((item: any, index) => (
                            <TypographyA
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: '1rem',
                                maxWidth: isWindow()
                                  ? '400px !important'
                                  : '200px !important'
                              }}
                              key={`${item.phenoName}_${index}`}
                            >
                              {`(${
                                item.inheritance ? item.inheritance : '.'
                              })-${
                                i18n && i18n.language === 'zh'
                                  ? item.pheno_name_cn + '-'
                                  : ''
                              }${item.phenoName}`}
                            </TypographyA>
                          ))}
                        </React.Fragment>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: isWindow() ? '400px' : '200px'
                        }
                      }}
                    >
                      <Box>
                        {OMIMValue.slice(0, 2).map((items: any, index) => {
                          return (
                            <TypographyA
                              sx={{
                                width: '100%',
                                maxWidth: 200,
                                lineHeight: '24px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color:
                                  bgcolor === 'rgb(109,56,149,0.8)' ||
                                  bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgba(229,38,35,0.8)'
                                    ? 'white'
                                    : '#23527c',
                                mb: '4px'
                              }}
                              onClick={() => {
                                props.setCurrent({
                                  ...row.original,
                                  omim_curate_phenotypes,
                                  omim_phenotypes
                                })

                                props.setOMIMOpen(true)
                              }}
                              key={`${items.phenoMim}_${index}`}
                            >
                              <Chip
                                label={`${
                                  items.inheritance ? items.inheritance : '.'
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}
                              />

                              <span
                                style={{
                                  lineHeight: '24px'
                                }}
                              >{`${
                                i18n && i18n.language === 'zh'
                                  ? items.pheno_name_cn + '-'
                                  : ''
                              }${items.phenoName}`}</span>
                            </TypographyA>
                          )
                        })}

                        {/* {OMIMValue.length > 3 ? '...' : ''} */}
                      </Box>
                    </HtmlTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            }
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'scip_summary_priority',
          id: 'scip_summary_priority',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'根据变异质量排序'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('优先级')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original?.scip_summary_priority}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'scip_summary_auto_result',
          id: 'scip_summary_auto_result ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip
                title={
                  <Box>
                    <Typography color={'white'} fontSize={12}>
                      质量:变异质量分级
                    </Typography>

                    <Typography color={'white'} fontSize={12}>
                      映射质量:变异质量评估参数，通常大于50
                    </Typography>

                    <Typography color={'white'} fontSize={12}>
                      深度比:目标区域与参考区域深度对比
                    </Typography>
                  </Box>
                }
              >
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('质量')}`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('映射质量')}`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('深度比')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>
                  {row.original.scip_summary_auto_result}
                </TypographyA>

                <TypographyA>
                  {row.original.scip_summary_mapping_quality}
                </TypographyA>

                <TypographyA>
                  {row.original.scip_summary_depth_ratio}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'sv_count',
          id: 'sv_count ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip
                title={
                  'The overlap rate of different databases can be calculated, and the overlap rate can be adjusted personalized'
                }
              >
                <>
                  <Stack maxWidth={'100%'}>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {`${t('The overlap rate is over')}90%`}
                    </span>
                  </Stack>

                  <Stack maxWidth={'100%'}>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {`${t('Number of cases')}`}
                    </span>
                  </Stack>
                </>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const scip_gnomad_sv_data = row.original?.scip_gnomad_sv_data
            const scip_clinvar_sv_data = row.original?.scip_clinvar_sv_data
            const decipherCnvInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo?.decipherCnvInfo
            const decipherSyndromeInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo
                ?.decipherSyndromeInfo
            const decipherPopulationInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo
                ?.decipherPopulationInfo
            const decipherDgvInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo?.decipherDgvInfo

            // const gnomads =
            //   scip_gnomad_sv_data && Array.isArray(scip_gnomad_sv_data)
            //     ? [...scip_gnomad_sv_data].filter(
            //         (i) => i && i.overlap_rate && i.overlap_rate >= 0.9
            //       )
            //     : []
            // const gnomadscount = gnomads?.length
            // const clinvars =
            //   scip_clinvar_sv_data && Array.isArray(scip_clinvar_sv_data)
            //     ? [...scip_clinvar_sv_data].filter(
            //         (i) => i && i.overlap_rate && i.overlap_rate >= 0.9
            //       )
            //     : []
            // const clinvarscount = clinvars?.length
            const gnomad =
              decipherPopulationInfo && Array.isArray(decipherPopulationInfo)
                ? [...decipherPopulationInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const gnomadscount = gnomad?.length
            const clinvar =
              decipherSyndromeInfo && Array.isArray(decipherSyndromeInfo)
                ? [...decipherSyndromeInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const clinvarscount = clinvar?.length
            const decipher =
              decipherCnvInfo && Array.isArray(decipherCnvInfo)
                ? [...decipherCnvInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const deciphercount = decipher?.length
            const dgv =
              decipherDgvInfo && Array.isArray(decipherDgvInfo)
                ? [...decipherDgvInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const dgvcount = dgv?.length

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
                spacing={1}
                sx={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  props.setCurrent({
                    ...row.original,
                    gnomads: gnomad,
                    clinvars: clinvar,
                    decipherCnvInfos: decipher,
                    decipherDgvInfos: dgv
                  })

                  props.setCaseNumberInfoOpen &&
                    props.setCaseNumberInfoOpen(true)
                }}
              >
                <TypographyA>{`gnomad:${gnomadscount}`}</TypographyA>

                <TypographyA>{`clinvar:${clinvarscount}`}</TypographyA>

                <TypographyA>{`decipher:${deciphercount}`}</TypographyA>

                <TypographyA>{`dgv:${dgvcount}`}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },

        // {
        //   accessorKey: 'DDD_HI_percent',
        //   id: 'DDD_HI_percent ',
        //   enableSorting: false,
        //   header: () => (
        //     <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
        //       <Stack maxWidth={'100%'}>
        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('DDD HI')}
        //         </span>

        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('LOEUF bin')}
        //         </span>

        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('GnomAD pLI')}
        //         </span>
        //       </Stack>
        //     </Stack>
        //   ),
        //   cell: ({ row, getValue }) => {
        //     return (
        //       <Stack maxWidth={'100%'} direction="column" spacing={1}>
        //         <TypographyA
        //           sx={{
        //             width: '100%',
        //             maxWidth: 140,
        //             overflow: 'hidden',
        //             whiteSpace: 'nowrap',
        //             textOverflow: 'ellipsis'
        //           }}
        //         >
        //           {`${row.original.DDD_HI_percent}`}
        //         </TypographyA>

        //         <TypographyA
        //           sx={{
        //             width: '100%',
        //             maxWidth: 140,
        //             overflow: 'hidden',
        //             whiteSpace: 'nowrap',
        //             textOverflow: 'ellipsis'
        //           }}
        //         >
        //           {`${row.original.LOEUF_bin}`}
        //         </TypographyA>

        //         <TypographyA
        //           sx={{
        //             width: '100%',
        //             maxWidth: 140,
        //             overflow: 'hidden',
        //             whiteSpace: 'nowrap',
        //             textOverflow: 'ellipsis'
        //           }}
        //         >
        //           {`${
        //             row.original.GnomAD_pLI === '.' ||
        //             row.original.GnomAD_pLI === 'NaN'
        //               ? '.'
        //               : (Number(row.original.GnomAD_pLI).toFixed(
        //                   4
        //                 ) as unknown as number) == 0
        //               ? 0
        //               : Number(row.original.GnomAD_pLI).toFixed(4)
        //           }`}
        //         </TypographyA>
        //       </Stack>
        //     )
        //   },
        //   footer: (props) => props.column.id
        // },

        // {
        //   accessorKey: 'clinvar_info',
        //   id: 'Clinvar_info',
        //   enableSorting: false,
        //   header: () => (
        //     <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
        //       <Stack maxWidth={'100%'}>
        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('clinvar Info')}
        //         </span>
        //       </Stack>
        //     </Stack>
        //   ),
        //   cell: ({ row, getValue }) => {
        //     const clinvar_info: any[] = row.original.clinvar_info ?? []

        //     return (
        //       <Stack maxWidth={'100%'} direction="column" spacing={1}>
        //         <TypographyA
        //           onClick={(e) => {
        //             if (clinvar_info.length) {
        //               props.setCurrent(row.original)

        //               props.handleMenuClick && props.handleMenuClick(e, 'clinvar_info')
        //             }
        //           }}
        //           sx={{
        //             cursor: 'pointer'
        //           }}
        //         >
        //           {clinvar_info.length}
        //         </TypographyA>
        //       </Stack>
        //     )
        //   },
        //   footer: (props) => props.column.id
        // },
        {
          accessorKey: 'scip_clingen_sv_data',
          id: 'scip_clingen_sv_data',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Tooltip title={'clingen数据库特殊区域描述'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('clingen')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const scip_clinical_genome_data: any[] =
              row.original.scip_clinical_genome_data ?? []

            return (
              <Tooltip
                title={scip_clinical_genome_data
                  .map((i) => `${i.dosageId}(${i.hgncId})`)
                  .join(';')}
              >
                <TypographyA
                  onClick={(e) => {
                    props.setCurrent(row.original)

                    // props.handleMenuClick && props.handleMenuClick(e, 'scip_clingen')

                    props.setClingenOpen && props.setClingenOpen(true)
                  }}
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                >
                  {scip_clinical_genome_data
                    .map((i) => `${i.dosageId}(${i.hgncId})`)
                    .join(';')}
                </TypographyA>
              </Tooltip>
            )
          },
          footer: (props) => props.column.id
        }
      ]
    } else {
      return [
        {
          accessorKey: 'chrom',
          id: 'chrom',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Chrom')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <TypographyA>{row.original.chrom}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'SV_start',
          id: 'SV_start',
          enableSorting: false,
          header: () => (
            <Stack
              maxWidth={'100%'}
              justifyContent="start"
              alignItems="center"
              direction={'row'}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Start')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('End ')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                sx={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  //
                  props.setIgvOpen && props.setIgvOpen(true)

                  props.setCurrent(row.original)

                  props.setIgvLocus &&
                    props.setIgvLocus(
                      `chr${row.original.chrom}:${row.original.SV_start}-${row.original.SV_end}`
                    )
                }}
              >
                {row.original.tempCnvManual &&
                row.original.tempCnvManual.type ? (
                  <TypographyA>{t('Merge')}</TypographyA>
                ) : (
                  ''
                )}

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {row.original.SV_start}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {row.original.SV_end}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'Chromosomearm',
          id: 'Chromosomearm',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Chromosome arm')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="center"
              >
                <TypographyA>{row.original.Chromosomearm}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'CN',
          id: 'CN',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Copy Number(CN)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="center"
              >
                <TypographyA>{row.original.CN}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'SVType',
          id: 'SVType ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Type')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original.SV_type}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'gene1',
          id: 'gene1',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'下拉箭头查看基因详情'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('Gene')}`}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const scip_gene_info_data = row.original?.scip_gene_info_data ?? []
            const omim_genes = row.original?.ExomeDepth_omim_genes?.join(';')
            const omim_disease_genes =
              row.original.gene_info?.omim_disease_genes
                .map((item) => {
                  return `${item.symbol}`
                })
                .join(';')
            const other_genes = row.original.gene_info?.other_genes
              ?.map((item) => {
                return `${item}`
              })
              .join(';')

            return (
              <Stack
                sx={{
                  position: 'relative'
                }}
                maxWidth={'160px'}
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Stack
                  maxWidth={'130px'}
                  width={'100%'}
                  marginTop={'12px'}
                  direction="column"
                  alignItems="start"
                  sx={{
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    props.setCnvAllOpen && props.setCnvAllOpen(true)

                    props.setCurrent(row.original)
                  }}
                >
                  <Tooltip
                    title={`${omim_disease_genes}`}
                    placement="top-start"
                  >
                    <TypographyA
                      sx={{
                        width: '100%',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-all',
                        fontWeight: 500
                      }}
                    >
                      {omim_disease_genes ? (
                        <>
                          <span
                            style={{
                              color: 'red'
                            }}
                          >{`${omim_disease_genes}`}</span>
                        </>
                      ) : null}
                    </TypographyA>
                  </Tooltip>

                  <Tooltip title={other_genes} placement="top-start">
                    <TypographyA
                      sx={{
                        width: '100%',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-all',
                        fontWeight: 500
                      }}
                    >
                      {other_genes ? (
                        <>
                          <span>{`${other_genes}`}</span>
                        </>
                      ) : null}
                    </TypographyA>
                  </Tooltip>
                </Stack>

                {scip_gene_info_data.length ? (
                  <IconButton
                    onClick={(e) => {
                      props.setCurrent(row.original)

                      props.handleMenuClick &&
                        props.handleMenuClick(e, 'scip_gene')
                    }}
                    sx={{
                      color: 'inherit',
                      padding: 0

                      // position: 'absolute',
                      // right: 0,
                      // bottom: '-1rem'
                    }}
                    size="small"
                  >
                    <MenuDown />
                  </IconButton>
                ) : (
                  ''
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'ClassifyCNV_evidences',
          id: 'ClassifyCNV_evidences ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('ACMG evidences')}(Annotsv)`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`ACMG ${t('score')}`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Length (bp)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.AnnotSV_evidences}</TypographyA>

                <TypographyA>{row.original.total_score}</TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {typeof row.original.LEN === 'number'
                    ? Math.round(row.original.LEN)
                    : row.original.LEN}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'ccnv_classification',
          id: 'ccnv_priority',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'双软件注释(classifyCNV、annotsv)'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('ACMG')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const classification = row.original.classification
            const flag =
              (classification?.toLowerCase() === 'likely pathogenic' ||
                classification?.toLowerCase() === 'pathogenic') &&
              classification !== row.original.ccnv_classification

            console.log(
              'flag',
              flag,
              classification,
              row.original.ccnv_classification
            )

            const bgcolor = GLScolor.find(
              (i) => i.key === row.original.ccnv_classification?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key === row.original.ccnv_classification?.toLowerCase()
                )?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'rgba(0, 0, 0)'

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="row"
                alignItems="center"
                justifyContent={'center'}
              >
                {row.original.ccnv_classification &&
                row.original.ccnv_classification != '.' ? (
                  <Chip
                    sx={{
                      bgcolor: bgcolor,
                      color:
                        bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
                      justifyContent: 'start',
                      fontSize: '0.75rem'
                    }}
                    label={`${
                      row.original.ccnv_classification &&
                      row.original.ccnv_classification != '.'
                        ? row.original.ccnv_classification
                            .split(' ')
                            .map((word) => {
                              if (word.includes('Uncertain')) return `VU`
                              if (word.includes('null')) return ``
                              else return word[0].toUpperCase()
                            })
                            .join('')
                        : '.'
                    }`}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setCnvScipOpen && props.setCnvScipOpen(true)
                    }}
                    size="small"
                  />
                ) : (
                  row.original.ccnv_classification
                )}

                {flag ? (
                  <Tooltip
                    placement="top-start"
                    title={`AnnotSV:${classification}`}
                  >
                    <Box
                      sx={{
                        width: '10px',
                        height: '10px',
                        bgcolor: 'red',
                        borderRadius: '50%',
                        display: 'inline-block'
                      }}
                    ></Box>
                  </Tooltip>
                ) : (
                  ''
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'scip_highlights_data',
          id: 'scip_highlights_data',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'良性信息(如高人群频率等)'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('负面信息情况')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>
                  {row.original?.scip_highlights_data?.negative?.filter(
                    (i: string) => !i.includes('没有异常读取')
                  ).length
                    ? t('true')
                    : t('false')}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'inheritance',
          id: 'inheritance',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="center" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('OMIM')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const omim_info = row.original.omim_info

            const bgcolor = color.find(
              (i) => i.key === row.original.ccnv_classification?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key === row.original.ccnv_classification?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            if (
              row.original.OMIMArr &&
              row.original.OMIMArr.length &&
              row.original.OMIMArr.some(
                (item: any) => item?.geneDiseaseTranslated?.data
              ) &&
              i18n &&
              i18n.language === 'zh'
            ) {
              const OMIMValue =
                row.original.OMIMArr.map(
                  (items: any) => items?.geneDiseaseTranslated?.data ?? []
                ).flat() ?? []

              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {OMIMValue && OMIMValue.length ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {OMIMValue.map((item: any, index: any) => (
                            <TypographyA
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: '1rem',
                                maxWidth: isWindow()
                                  ? '400px !important'
                                  : '200px !important'
                              }}
                              key={`${item.disease}_${index}`}
                            >
                              {`(${
                                item.genetic_mode ? item.genetic_mode : '.'
                              })-${item.disease}`}
                            </TypographyA>
                          ))}
                        </React.Fragment>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: isWindow() ? '400px' : '200px'
                        }
                      }}
                    >
                      <Box>
                        {OMIMValue.slice(0, 2).map((items: any, index: any) => {
                          return (
                            <TypographyA
                              sx={{
                                width: '100%',
                                maxWidth: 200,
                                lineHeight: '24px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color: '#23527c',
                                mb: '4px'
                              }}
                              onClick={() => {
                                props.setCurrent(row.original)

                                props.setOMIMOpen(true)
                              }}
                              key={`${items.disease}_${index}`}
                            >
                              <Chip
                                label={`${
                                  items.genetic_mode ? items.genetic_mode : '.'
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.75rem'
                                }}
                              />

                              <span
                                style={{
                                  lineHeight: '24px'
                                }}
                              >{`${items.disease}`}</span>
                            </TypographyA>
                          )
                        })}

                        {/* {OMIMValue.length > 3 ? '...' : ''} */}
                      </Box>
                    </HtmlTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            } else if (row.original.OMIMArr && row.original.OMIMArr.length) {
              let OMIMValue =
                row.original.OMIMArr.map((items: any) => items?.omimData ?? [])
                  .flat()
                  .map((item: any) => {
                    const inheritances: string[] = []

                    if (
                      item &&
                      item.clinicalSynopsis &&
                      item.clinicalSynopsis.INHERITANCE &&
                      Array.isArray(item.clinicalSynopsis.INHERITANCE)
                    ) {
                      item.clinicalSynopsis.INHERITANCE.forEach((str: string) =>
                        inheritances.push(str.toLowerCase())
                      )
                    }
                    const inheritance = formatVariantInheritance(
                      Array.from([...new Set(inheritances)]).join(',')
                    )

                    return {
                      ...item,
                      inheritance
                    }
                  }) ?? []

              OMIMValue = unique(OMIMValue, 'phenoName')

              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {OMIMValue && OMIMValue.length ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {OMIMValue.map((item: any, index: any) => (
                            <TypographyA
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: '1rem',
                                maxWidth: isWindow()
                                  ? '400px !important'
                                  : '200px !important'
                              }}
                              key={`${item.phenoName}_${index}`}
                            >
                              {`(${
                                item.inheritance ? item.inheritance : '.'
                              })-${
                                i18n && i18n.language === 'zh'
                                  ? item.pheno_name_cn + '-'
                                  : ''
                              }${item.phenoName}`}
                            </TypographyA>
                          ))}
                        </React.Fragment>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: isWindow() ? '400px' : '200px'
                        }
                      }}
                    >
                      <Box>
                        {OMIMValue.slice(0, 2).map((items: any, index: any) => {
                          return (
                            <TypographyA
                              sx={{
                                width: '100%',
                                maxWidth: 200,
                                lineHeight: '24px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color: '#23527c',
                                mb: '4px'
                              }}
                              onClick={() => {
                                props.setCurrent(row.original)

                                props.setOMIMOpen(true)
                              }}
                              key={`${items.phenoMim}_${index}`}
                            >
                              <Chip
                                label={`${
                                  items.inheritance ? items.inheritance : '.'
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.75rem'
                                }}
                              />

                              <span
                                style={{
                                  lineHeight: '24px'
                                }}
                              >{`${
                                i18n && i18n.language === 'zh'
                                  ? items.pheno_name_cn + '-'
                                  : ''
                              }${items.phenoName}`}</span>
                            </TypographyA>
                          )
                        })}

                        {/* {OMIMValue.length > 3 ? '...' : ''} */}
                      </Box>
                    </HtmlTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            } else
              return (
                <Stack
                  maxWidth={'150px'}
                  width={'100%'}
                  marginTop={'12px'}
                  direction="column"
                  alignItems="start"
                >
                  <Tooltip
                    title={
                      <Box maxHeight={300} overflow="auto">
                        {omim_info?.map((item: any, index: any) => (
                          <TypographyA key={`${item.pheno_name_cn}_${index}`}>
                            {`(${item.inheritance ? item.inheritance : '.'})-${
                              item.pheno_name_cn
                            }`}
                          </TypographyA>
                        ))}
                      </Box>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        maxWidth: isWindow() ? '400px' : '200px'
                      }
                    }}
                  >
                    <Box width={'100%'}>
                      {omim_info?.slice(0, 5).map((items: any, index: any) => {
                        return (
                          <TypographyA
                            sx={{
                              width: '100%',
                              maxWidth: 300,
                              lineHeight: '24px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              color:
                                bgcolor === 'rgb(109,56,149,0.8)' ||
                                bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : '#23527c',
                              mb: '4px'
                            }}
                            onClick={() => {
                              props.setCurrent(row.original)

                              props.setOMIMOpen(true)
                            }}
                            key={`${items.pheno_name_cn}_${index}`}
                          >
                            <Chip
                              label={`${
                                items.inheritance ? items.inheritance : '.'
                              }`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.75rem'
                              }}
                            />

                            <span
                              style={{
                                lineHeight: '24px'
                              }}
                            >{`${items.pheno_name_cn}`}</span>
                          </TypographyA>
                        )
                      })}
                    </Box>
                  </Tooltip>
                </Stack>
              )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'scip_summary_priority',
          id: 'scip_summary_priority',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'根据变异质量排序'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('优先级')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original?.scip_summary_priority}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'scip_summary_auto_result',
          id: 'scip_summary_auto_result ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip
                title={
                  <Box>
                    <Typography color={'white'} fontSize={12}>
                      质量:变异质量分级
                    </Typography>

                    <Typography color={'white'} fontSize={12}>
                      映射质量:变异质量评估参数，通常大于50
                    </Typography>

                    <Typography color={'white'} fontSize={12}>
                      深度比:目标区域与参考区域深度对比
                    </Typography>
                  </Box>
                }
              >
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('质量')}`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('映射质量')}`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('深度比')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>
                  {row.original.scip_summary_auto_result}
                </TypographyA>

                <TypographyA>
                  {row.original.scip_summary_mapping_quality}
                </TypographyA>

                <TypographyA>
                  {row.original.scip_summary_depth_ratio}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'P_snvindel_nb',
          id: 'P_snvindel_nb',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Tooltip title={'区域内致病变异数量'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    Pathogenic
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    SNV/INDEL
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    overlapped
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <Tooltip
                  title={`${row.original.P_snvindel_nb}`}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {`${row.original.P_snvindel_nb}`}
                  </TypographyA>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'sv_count',
          id: 'sv_count ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip
                title={
                  'The overlap rate of different databases can be calculated, and the overlap rate can be adjusted personalized'
                }
              >
                <>
                  <Stack maxWidth={'100%'}>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {`${t('The overlap rate is over')}90%`}
                    </span>
                  </Stack>

                  <Stack maxWidth={'100%'}>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {`${t('Number of cases')}`}
                    </span>
                  </Stack>
                </>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const scip_gnomad_sv_data = row.original?.scip_gnomad_sv_data
            const scip_clinvar_sv_data = row.original?.scip_clinvar_sv_data
            const decipherCnvInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo?.decipherCnvInfo
            const decipherSyndromeInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo
                ?.decipherSyndromeInfo
            const decipherPopulationInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo
                ?.decipherPopulationInfo
            const decipherDgvInfo =
              row.original?.tempCnvDecipherInfo?.decipherInfo?.decipherDgvInfo

            // const gnomads =
            //   scip_gnomad_sv_data && Array.isArray(scip_gnomad_sv_data)
            //     ? [...scip_gnomad_sv_data].filter(
            //         (i) => i && i.overlap_rate && i.overlap_rate >= 0.9
            //       )
            //     : []
            // const gnomadscount = gnomads?.length
            // const clinvars =
            //   scip_clinvar_sv_data && Array.isArray(scip_clinvar_sv_data)
            //     ? [...scip_clinvar_sv_data].filter(
            //         (i) => i && i.overlap_rate && i.overlap_rate >= 0.9
            //       )
            //     : []
            // const clinvarscount = clinvars?.length
            const gnomad =
              decipherPopulationInfo && Array.isArray(decipherPopulationInfo)
                ? [...decipherPopulationInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const gnomadscount = gnomad?.length
            const clinvar =
              decipherSyndromeInfo && Array.isArray(decipherSyndromeInfo)
                ? [...decipherSyndromeInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const clinvarscount = clinvar?.length
            const decipher =
              decipherCnvInfo && Array.isArray(decipherCnvInfo)
                ? [...decipherCnvInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const deciphercount = decipher?.length
            const dgv =
              decipherDgvInfo && Array.isArray(decipherDgvInfo)
                ? [...decipherDgvInfo]
                    .filter(
                      (i) =>
                        switchVariantClass(i.variant_class) ===
                        row.original.sv_type
                    )
                    .map((i) => {
                      i.Coverage = calculateOverlapRate(
                        row.original.start,
                        row.original.end,
                        i.start,
                        i.end
                      )

                      return i
                    })
                    .filter((i) => i && i.Coverage && i.Coverage >= 0.9)
                : []

            const dgvcount = dgv?.length

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
                spacing={1}
                sx={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  props.setCurrent({
                    ...row.original,
                    gnomads: gnomad,
                    clinvars: clinvar,
                    decipherCnvInfos: decipher,
                    decipherDgvInfos: dgv
                  })

                  props.setCaseNumberInfoOpen &&
                    props.setCaseNumberInfoOpen(true)
                }}
              >
                <TypographyA>{`gnomad:${gnomadscount}`}</TypographyA>

                <TypographyA>{`clinvar:${clinvarscount}`}</TypographyA>

                <TypographyA>{`decipher:${deciphercount}`}</TypographyA>

                <TypographyA>{`dgv:${dgvcount}`}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },

        // {
        //   accessorKey: 'DDD_HI_percent',
        //   id: 'DDD_HI_percent ',
        //   enableSorting: false,
        //   header: () => (
        //     <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
        //       <Stack maxWidth={'100%'}>
        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('DDD HI')}
        //         </span>

        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('LOEUF bin')}
        //         </span>

        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('GnomAD pLI')}
        //         </span>
        //       </Stack>
        //     </Stack>
        //   ),
        //   cell: ({ row, getValue }) => {
        //     return (
        //       <Stack maxWidth={'100%'} direction="column" spacing={1}>
        //         <TypographyA
        //           sx={{
        //             width: '100%',
        //             maxWidth: 140,
        //             overflow: 'hidden',
        //             whiteSpace: 'nowrap',
        //             textOverflow: 'ellipsis'
        //           }}
        //         >
        //           {`${row.original.DDD_HI_percent}`}
        //         </TypographyA>

        //         <TypographyA
        //           sx={{
        //             width: '100%',
        //             maxWidth: 140,
        //             overflow: 'hidden',
        //             whiteSpace: 'nowrap',
        //             textOverflow: 'ellipsis'
        //           }}
        //         >
        //           {`${row.original.LOEUF_bin}`}
        //         </TypographyA>

        //         <TypographyA
        //           sx={{
        //             width: '100%',
        //             maxWidth: 140,
        //             overflow: 'hidden',
        //             whiteSpace: 'nowrap',
        //             textOverflow: 'ellipsis'
        //           }}
        //         >
        //           {`${
        //             row.original.GnomAD_pLI === '.' ||
        //             row.original.GnomAD_pLI === 'NaN'
        //               ? '.'
        //               : (Number(row.original.GnomAD_pLI).toFixed(
        //                   4
        //                 ) as unknown as number) == 0
        //               ? 0
        //               : Number(row.original.GnomAD_pLI).toFixed(4)
        //           }`}
        //         </TypographyA>
        //       </Stack>
        //     )
        //   },
        //   footer: (props) => props.column.id
        // },

        // {
        //   accessorKey: 'clinvar_info',
        //   id: 'Clinvar_info',
        //   enableSorting: false,
        //   header: () => (
        //     <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
        //       <Stack maxWidth={'100%'}>
        //         <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
        //           {t('clinvar Info')}
        //         </span>
        //       </Stack>
        //     </Stack>
        //   ),
        //   cell: ({ row, getValue }) => {
        //     const clinvar_info: any[] = row.original.clinvar_info ?? []

        //     return (
        //       <Stack maxWidth={'100%'} direction="column" spacing={1}>
        //         <TypographyA
        //           onClick={(e) => {
        //             if (clinvar_info.length) {
        //               props.setCurrent(row.original)

        //               props.handleMenuClick && props.handleMenuClick(e, 'clinvar_info')
        //             }
        //           }}
        //           sx={{
        //             cursor: 'pointer'
        //           }}
        //         >
        //           {clinvar_info.length}
        //         </TypographyA>
        //       </Stack>
        //     )
        //   },
        //   footer: (props) => props.column.id
        // },
        {
          accessorKey: 'scip_clingen_sv_data',
          id: 'scip_clingen_sv_data',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Tooltip title={'clingen数据库特殊区域描述'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('clingen')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const scip_clingen_sv_data: any[] =
              row.original.scip_clingen_sv_data ?? []
            const scip_clinical_genome_data: any[] =
              row.original.scip_clinical_genome_data ?? []

            return (
              <Tooltip
                title={scip_clinical_genome_data
                  .map((i) => `${i.dosageId}(${i.hgncId})`)
                  .join(';')}
              >
                <TypographyA
                  onClick={(e) => {
                    props.setCurrent(row.original)

                    // props.handleMenuClick && props.handleMenuClick(e, 'scip_clingen')

                    props.setClingenOpen && props.setClingenOpen(true)
                  }}
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                >
                  {scip_clinical_genome_data
                    .map((i) => `${i.dosageId}(${i.hgncId})`)
                    .join(';')}
                </TypographyA>
              </Tooltip>
            )
          },
          footer: (props) => props.column.id
        }
      ]
    }
  }, [props])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default columns1CNV
